import { Divider, Stack, useTheme } from "@mui/material";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getPortfolioReportingSummaryQuery } from "web_myplann/Api/Portfolio/Queries/getPortfolioReportingSummaryQuery";
import { usePortfolioListQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioListQuery";
import { usePropertyListQuery } from "web_myplann/Api/Portfolio/Queries/usePropertyListQuery";
import {
	type ClientPortfolioDomainEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { GroupedListLabel } from "web_myplann/Portfolio/Components/GroupedListLabel";
import { GroupedPortfolioListItem } from "web_myplann/Portfolio/Components/GroupedPortfolioListItem";
import { PortfolioListItemTitle } from "web_myplann/Portfolio/Components/PortfolioSections/PortfolioListItemTitle";
import { PropertyListItemTitle } from "web_myplann/Portfolio/Components/PropertySections/PropertyListItemTitle";
import {
	type SortKey,
	SortTypes,
} from "web_myplann/Portfolio/Components/SortDrawer";
import { REPORTING_VALUE_DOMAINS } from "web_myplann/Portfolio/Helpers/portfolioReportingDomains";
import {
	type GroupedPortfolioList,
	useGroupPortfolio,
} from "web_myplann/Portfolio/Hooks/useGroupPortfolio";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";

export type PortfolioSubType =
	| ClientPortfolioDomainEnum
	| ClientPropertyTypeEnum;

type Props = {
	activeSort: SortKey;
	setIsLoading: (isFetching: boolean) => void;
};

export const PortfolioListPage: React.FunctionComponent<Props> = ({
	activeSort,
	setIsLoading,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();

	const [specialPortfolioListIds, setSpecialPortfolioListIds] = useState<
		number[]
	>([]);

	const { data: portfolioList, isLoading: isPortfolioLoading } =
		usePortfolioListQuery();
	const { data: propertyList, isLoading: isPropertyLoading } =
		usePropertyListQuery();
	const { data: portfolioReportingSummary } = getPortfolioReportingSummaryQuery(
		specialPortfolioListIds,
	);

	useEffect(() => {
		if (portfolioList?.data) {
			const newIds =
				portfolioList.data
					.filter(
						(p) =>
							p.domain !== undefined &&
							p.domain !== null &&
							REPORTING_VALUE_DOMAINS.includes(p.domain),
					)
					.map((p) => p.id) ?? [];

			setSpecialPortfolioListIds(newIds);
		}
	}, [portfolioList?.data]);

	const groupedPortfolioList = useGroupPortfolio(portfolioList?.data ?? []);

	const groupedPortfolioListWithoutServicesAndUncategorized: GroupedPortfolioList =
		useMemo(() => {
			const {
				Services: _Services,
				Uncategorized: _Uncategorized,
				...rest
			} = groupedPortfolioList;
			return rest;
		}, [groupedPortfolioList]);

	const groupedPortfolioListServicesAndUncategorized: GroupedPortfolioList =
		useMemo(
			() => ({
				Services: groupedPortfolioList.Services ?? [],
				Uncategorized: groupedPortfolioList.Uncategorized ?? [],
			}),
			[groupedPortfolioList],
		);

	useEffect(() => {
		setIsLoading(isPortfolioLoading && isPropertyLoading);
	}, [isPortfolioLoading, isPropertyLoading, setIsLoading]);

	const sortedPortfolioData = useMemo(() => {
		if (activeSort === SortTypes.producer) {
			return (
				portfolioList?.data.sort(
					(a, b) => a.producer?.localeCompare(b.producer ?? "") ?? 0,
				) ?? []
			);
		}

		if (activeSort === SortTypes.date) {
			return (
				portfolioList?.data.sort(
					(a, b) =>
						new Date(a.endAt ?? "").getTime() -
						new Date(b.endAt ?? "").getTime(),
				) ?? []
			);
		}

		return [];
	}, [portfolioList?.data, activeSort]);

	const sortedPropertyData = useMemo(() => {
		const propertyOrder = [
			ClientPropertyTypeEnum.AutoMoto,
			ClientPropertyTypeEnum.Flat,
			ClientPropertyTypeEnum.House,
			ClientPropertyTypeEnum.Cottage,
			ClientPropertyTypeEnum.Commodity,
			ClientPropertyTypeEnum.Land,
			ClientPropertyTypeEnum.Art,
		];

		return propertyList?.data.sort(
			(a, b) =>
				propertyOrder.indexOf(a.property ?? ClientPropertyTypeEnum.Commodity) -
				propertyOrder.indexOf(b.property ?? ClientPropertyTypeEnum.Commodity),
		);
	}, [propertyList?.data]);

	const navigateToPortfolioDetail = (id: number) => {
		navigate(Routing.Portfolio.PortfolioDetail.path(id.toString()));
	};

	const navigateToPropertyDetail = (id: number) => {
		navigate(Routing.Portfolio.PropertyDetail.path(id.toString()));
	};

	const isDataEmpty =
		portfolioList?.data &&
		portfolioList?.data?.length === 0 &&
		propertyList?.data &&
		propertyList?.data?.length === 0 &&
		!isPortfolioLoading &&
		!isPropertyLoading;

	return (
		<Stack data-testid="portfolio-list-page">
			{isDataEmpty ? (
				<EmptyListSkeleton
					label={t(Resources.Portfolio.List.Skeleton.label)}
					description={t(Resources.Portfolio.List.Skeleton.description)}
				/>
			) : (
				<>
					{activeSort === SortTypes.default ? (
						Object.keys(
							groupedPortfolioListWithoutServicesAndUncategorized,
						).map((item) => (
							<GroupedPortfolioListItem
								key={item}
								item={item}
								list={groupedPortfolioListWithoutServicesAndUncategorized}
								navigateToPortfolioDetail={navigateToPortfolioDetail}
								summaryData={portfolioReportingSummary?.data}
							/>
						))
					) : (
						<Stack gap={1.6} mt={1.2}>
							{sortedPortfolioData?.map((r) => (
								<Stack key={r.id} gap={1.6}>
									<PortfolioListItemTitle
										data={r}
										summaryData={portfolioReportingSummary?.data}
										onClick={() => navigateToPortfolioDetail(r.id)}
									/>
									<Divider
										sx={{
											borderWidth: 0.75,
											borderColor: theme.palette.deprecated.grey[100],
										}}
									/>
								</Stack>
							))}
						</Stack>
					)}

					{activeSort === SortTypes.default ? (
						(sortedPropertyData ?? []).length > 0 ? (
							<Stack>
								<GroupedListLabel
									title={t(Resources.Portfolio.List.Property)}
								/>

								<Stack gap={1.6} mt={1.2}>
									{sortedPropertyData?.map((r, index) => (
										<Stack key={r.id} gap={1.6}>
											<PropertyListItemTitle
												data={r}
												onClick={() => navigateToPropertyDetail(r.id)}
											/>
											<Stack
												px={index === sortedPropertyData.length - 1 ? 0 : 1.6}
												pr={
													index === sortedPropertyData.length - 1
														? 0.8
														: undefined
												}
											>
												<Divider
													sx={{
														borderWidth: 0.75,
														borderColor: theme.palette.deprecated.grey[100],
													}}
												/>
											</Stack>
										</Stack>
									))}
								</Stack>
							</Stack>
						) : null
					) : (sortedPropertyData ?? []).length > 0 ? (
						<Stack gap={1.6} mt={1.2}>
							{sortedPropertyData?.map((r) => (
								<Stack key={r.id} gap={1.6}>
									<PropertyListItemTitle
										data={r}
										onClick={() => navigateToPropertyDetail(r.id)}
									/>
									<Divider
										sx={{
											borderWidth: 0.75,
											borderColor: theme.palette.deprecated.grey[100],
										}}
									/>
								</Stack>
							))}
						</Stack>
					) : null}

					{activeSort === SortTypes.default
						? Object.keys(groupedPortfolioListServicesAndUncategorized).map(
								(item) => (
									<GroupedPortfolioListItem
										key={item}
										item={item}
										list={groupedPortfolioListServicesAndUncategorized}
										navigateToPortfolioDetail={navigateToPortfolioDetail}
										summaryData={portfolioReportingSummary?.data}
									/>
								),
							)
						: null}
				</>
			)}
		</Stack>
	);
};
